/**
 * Standalone suit fit sizing tool — jacket-first, 6" drop, ±2" set tolerance.
 * Analytics: dataLayer (GA4) + optional Meta fbq trackCustom.
 */
(function () {
  'use strict';

  const LENGTH_TO_CHART = {
    Short: 'Short',
    Regular: 'Regular',
    Long: 'Large',
  };

  const DROP_TOLERANCE = 2;

  /**
   * @param {string} eventName
   * @param {Record<string, unknown>} [params]
   */
  function pushAnalytics(eventName, params = {}) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...params });

    if (typeof window.fbq === 'function') {
      const metaPayload = { event_name: eventName, ...params };
      window.fbq('trackCustom', eventName, metaPayload);
    }
  }

  /**
   * @param {string} lengthLabel
   * @param {Array<{ 'suit-size': string, chest: string, waist: string }>} rows
   * @param {number} userChest
   * @param {number} userWaist
   */
  function computeRecommendation(lengthLabel, rows, userChest, userWaist) {
    if (!rows || rows.length === 0) {
      return { outcome_type: 'error', message: 'No sizing data for this length.' };
    }

    const chests = rows.map((r) => Number.parseFloat(r.chest));
    const minChest = Math.min(...chests);
    const maxChest = Math.max(...chests);

    if (userChest > maxChest) {
      const cap =
        lengthLabel === 'Short'
          ? '46S'
          : lengthLabel === 'Long'
            ? '54L'
            : '54R';
      return {
        outcome_type: 'error_chest_above_range',
        message: `Our largest jacket in this length is about a ${cap}. For larger chest measurements, please contact us — we may be able to help another way.`,
      };
    }

    const effectiveChest = userChest < minChest ? minChest : userChest;

    let best = rows[0];
    let bestDiff = Math.abs(Number.parseFloat(best.chest) - effectiveChest);
    for (const row of rows) {
      const c = Number.parseFloat(row.chest);
      const d = Math.abs(c - effectiveChest);
      if (d < bestDiff || (d === bestDiff && c < Number.parseFloat(best.chest))) {
        best = row;
        bestDiff = d;
      }
    }

    const jacketChest = Number.parseFloat(best.chest);
    const pairedWaist = Number.parseFloat(best.waist);
    const waistDelta = userWaist - pairedWaist;
    const absDelta = Math.abs(waistDelta);

    let outcome_type = 'set_only';
    if (absDelta > DROP_TOLERANCE) {
      outcome_type = 'set_plus_separate_pants';
    }

    const waist_delta_bucket = absDelta <= DROP_TOLERANCE ? 'within_2' : 'over_2';

    const tailoring = [];
    if (outcome_type === 'set_only' && absDelta > 0 && absDelta <= DROP_TOLERANCE) {
      if (waistDelta > 0) {
        tailoring.push(
          'Your waist is a bit larger than the pants paired with this jacket. Matching suit pants can often be let out up to about 2″ in the waistband.',
        );
      } else {
        tailoring.push(
          'Your waist is a bit smaller than the pants paired with this jacket. A tailor can usually take in the waist up to about 2″.',
        );
      }
    }

    if (userChest < minChest) {
      tailoring.push(
        `Your chest measurement is below the smallest jacket we list in ${lengthLabel} (${minChest}″). We started from that smallest size — try on to confirm.`,
      );
    }

    const suitSizeDisplay = String(best['suit-size']).replace('/', ' / ');

    return {
      outcome_type,
      waist_delta_bucket,
      suit_size_raw: best['suit-size'],
      suit_size_display: suitSizeDisplay,
      jacket_chest: jacketChest,
      paired_waist: pairedWaist,
      waist_delta: waistDelta,
      tailoring,
      disclaimer:
        'This tool estimates a starting size. Fit varies by brand and build — use as a guide, not a guarantee.',
    };
  }

  /**
   * @param {HTMLElement} root
   */
  function init(root) {
    const chartUrl = root.dataset.chartUrl;
    const suitsUrl = root.dataset.suitsUrl || '/collections/all';
    const pantsUrl = root.dataset.pantsUrl || '/collections/all';

    const stepWelcome = root.querySelector('[data-step="welcome"]');
    const stepLength = root.querySelector('[data-step="length"]');
    const stepMeasure = root.querySelector('[data-step="measurements"]');
    const stepResult = root.querySelector('[data-step="result"]');

    const btnStart = root.querySelector('[data-action="start"]');
    const btnBackWelcome = root.querySelector('[data-action="back-welcome"]');
    const btnNextLength = root.querySelector('[data-action="next-length"]');
    const btnBackLength = root.querySelector('[data-action="back-length"]');
    const btnNextMeasure = root.querySelector('[data-action="next-measure"]');
    const btnBackMeasure = root.querySelector('[data-action="back-measure"]');
    const btnRestart = root.querySelector('[data-action="restart"]');

    const resultTitle = root.querySelector('[data-outcome="title"]');
    const resultBody = root.querySelector('[data-outcome="body"]');
    const resultTailoring = root.querySelector('[data-outcome="tailoring"]');
    const linkSuits = root.querySelector('[data-cta="suits"]');
    const linkPants = root.querySelector('[data-cta="pants"]');

    if (linkSuits) linkSuits.href = suitsUrl;
    if (linkPants) linkPants.href = pantsUrl;

    let chartData = null;
    let currentLength = 'Regular';

    const showStep = (name) => {
      for (const el of [stepWelcome, stepLength, stepMeasure, stepResult]) {
        if (!el) continue;
        el.hidden = el.getAttribute('data-step') !== name;
      }
    };

    const loadChart = async () => {
      if (!chartUrl) return;
      const res = await fetch(chartUrl, { credentials: 'same-origin' });
      if (!res.ok) throw new Error('Chart load failed');
      chartData = await res.json();
    };

    btnStart?.addEventListener('click', () => {
      pushAnalytics('fit_tool_started', { tool_version: 'v1' });
      showStep('length');
    });

    btnBackWelcome?.addEventListener('click', () => {
      showStep('welcome');
    });

    btnNextLength?.addEventListener('click', () => {
      const selected = root.querySelector('input[name="suit_fit_length"]:checked');
      currentLength = selected ? selected.value : 'Regular';
      pushAnalytics('fit_tool_step_completed', {
        step_name: 'length',
        length_code: currentLength,
      });
      showStep('measurements');
    });

    btnBackLength?.addEventListener('click', () => {
      showStep('length');
    });

    btnNextMeasure?.addEventListener('click', async () => {
      const chestInput = root.querySelector('input[name="suit_fit_chest"]');
      const waistInput = root.querySelector('input[name="suit_fit_waist"]');
      const chestVal = Number.parseFloat(String(chestInput?.value ?? '').trim());
      const waistVal = Number.parseFloat(String(waistInput?.value ?? '').trim());

      if (!Number.isFinite(chestVal) || !Number.isFinite(waistVal)) {
        chestInput?.setAttribute('aria-invalid', 'true');
        waistInput?.setAttribute('aria-invalid', 'true');
        return;
      }
      chestInput?.setAttribute('aria-invalid', 'false');
      waistInput?.setAttribute('aria-invalid', 'false');

      pushAnalytics('fit_tool_step_completed', {
        step_name: 'measurements',
        length_code: currentLength,
      });

      try {
        if (!chartData) await loadChart();
      } catch {
        resultTitle.textContent = 'Something went wrong';
        resultBody.innerHTML =
          '<p>We could not load sizing data. Please refresh the page and try again.</p>';
        resultTailoring.innerHTML = '';
        pushAnalytics('fit_tool_result_shown', { outcome_type: 'error_chart_load' });
        showStep('result');
        requestAnimationFrame(() => resultTitle?.focus());
        return;
      }

      const chartKey = LENGTH_TO_CHART[currentLength] || 'Regular';
      const rows = chartData[chartKey];
      const rec = computeRecommendation(currentLength, rows, chestVal, waistVal);

      pushAnalytics('fit_tool_result_shown', {
        outcome_type: rec.outcome_type,
        length_code: currentLength,
        waist_delta_bucket: rec.waist_delta_bucket ?? '',
        recommended_suit_size: rec.suit_size_raw ?? '',
      });

      if (rec.outcome_type === 'error' || rec.outcome_type === 'error_chest_above_range') {
        resultTitle.textContent = 'We need a bit more info';
        resultBody.innerHTML = `<p>${rec.message}</p>`;
        resultTailoring.innerHTML = '';
        showStep('result');
        requestAnimationFrame(() => resultTitle?.focus());
        return;
      }

      resultTitle.textContent = 'Your starting point';

      if (rec.outcome_type === 'set_only') {
        resultBody.innerHTML = `
          <p><strong>Suit set to try first:</strong> ${rec.suit_size_display}</p>
          <p>We matched the <strong>jacket to your chest</strong> for ${currentLength} length. The pants in this set are paired on a standard <strong>6″ drop</strong> (about a ${rec.paired_waist}″ waist with a ${rec.jacket_chest}″ jacket).</p>
          <p>Your measured waist (${waistVal}″) is within <strong>±2″</strong> of that pairing, so a single suit set is a reasonable starting point.</p>
        `;
      } else {
        resultBody.innerHTML = `
          <p><strong>Jacket / suit set to try first:</strong> ${rec.suit_size_display}</p>
          <p>We matched the <strong>jacket to your chest</strong> for ${currentLength} length. The pants that ship with this set are built for about a <strong>${rec.paired_waist}″</strong> waist (6″ drop from the jacket numeral).</p>
          <p>Your measured waist is <strong>more than 2″ away</strong> from that paired waist, so sizing up the whole suit usually makes the jacket too large. Instead, plan on this suit size for the jacket, and add <strong>separate dress pants</strong> in a neutral color in your waist size (<strong>${waistVal}″</strong>).</p>
        `;
      }

      if (rec.tailoring.length) {
        resultTailoring.innerHTML = `<h3 class="suit-fit-sizing-tool__subhead">Tailoring notes</h3><ul>${rec.tailoring.map((t) => `<li>${t}</li>`).join('')}</ul>`;
      } else {
        resultTailoring.innerHTML = '';
      }

      const disc = document.createElement('p');
      disc.className = 'suit-fit-sizing-tool__disclaimer';
      disc.textContent = rec.disclaimer;
      resultTailoring.appendChild(disc);

      try {
        sessionStorage.setItem(
          'dh_fit_tool_result',
          JSON.stringify({
            suit_size: rec.suit_size_raw,
            outcome: rec.outcome_type,
            length: currentLength,
            ts: Date.now(),
          }),
        );
      } catch {
        /* ignore */
      }

      showStep('result');
      requestAnimationFrame(() => resultTitle?.focus());
    });

    btnBackMeasure?.addEventListener('click', () => {
      showStep('length');
    });

    btnRestart?.addEventListener('click', () => {
      showStep('welcome');
      root.querySelector('form')?.reset();
    });

    const ctaHandler = (e) => {
      const a = e.target.closest('a[data-cta]');
      if (!a) return;
      pushAnalytics('fit_tool_cta_clicked', { cta_id: a.getAttribute('data-cta') || 'unknown' });
    };
    root.addEventListener('click', ctaHandler);

    showStep('welcome');
  }

  document.querySelectorAll('[data-suit-fit-sizing-tool]').forEach((root) => {
    if (!(root instanceof HTMLElement)) return;
    init(root);
  });
})();
